import { Category, Product, CategoryUI, ProductUI } from "../types";
import "dotenv";
import { API_BASE_URL } from "./apiBaseUrl";

interface ProductsResponse {
  products: ProductUI[];
  totalItems: number;
  totalPages: number;
}

class CatalogApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  private transformCategory(category: Category): CategoryUI {
    return {
      id: category.ID != null ? String(category.ID) : "",
      name: category.NAME,
      outcode: category.OUTCODE,
      parent: category.PARENT != null ? String(category.PARENT) : null,
      description: category.DESCRIPTION,
      photo:
        category.SMALL_PHOTO || category.EXTERN_PHOTO || category.GENER_PHOTO,
    };
  }

  private transformProduct(product: Product): ProductUI {
    return {
      // ID из PHP/JSON может прийти числом — всегда нормализуем в string.
      id: product.ID != null ? String(product.ID) : "",
      name: product.NAME,
      price: product.PRICE,
      price1: product.PRICE1 && product.PRICE1 > 0 ? product.PRICE1 : undefined,
      price2: product.PRICE2 && product.PRICE2 > 0 ? product.PRICE2 : undefined,
      discCount1:
        product.DISC_COUNT1 && product.DISC_COUNT1 > 0
          ? product.DISC_COUNT1
          : undefined,
      discCount2:
        product.DISC_COUNT2 && product.DISC_COUNT2 > 0
          ? product.DISC_COUNT2
          : undefined,
      weightOfTheFish:
        product.WEIGHT_OF_THE_FISH && product.WEIGHT_OF_THE_FISH > 0
          ? product.WEIGHT_OF_THE_FISH
          : undefined,
      msUnit: product.MSUNIT || undefined,
      description: product.DESCRIPTION || "",
      categoryId:
        product.ID_GOODSGROUP != null ? String(product.ID_GOODSGROUP) : "",
      photo: product.SMALL_PHOTO || product.EXTERN_PHOTO || product.GENER_PHOTO,
      article: product.ARTICLE,
      outcode: product.OUTCODE,
      bonusFree: product.BONUS_FREE,
    };
  }

  async getCategories(
    city?: string,
    outletId?: string | null
  ): Promise<CategoryUI[]> {
    try {
      const params = new URLSearchParams({});

      if (city) {
        params.append("city", city);
      }
      if (outletId) {
        params.append("outlet_id", outletId);
      }

      const response = await fetch(`${this.baseUrl}/GetCategories.php?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data && data.categories && Array.isArray(data.categories)) {
        return data.categories.map((category: Category) =>
          this.transformCategory(category)
        );
      }

      if (Array.isArray(data)) {
        return data.map((category: Category) =>
          this.transformCategory(category)
        );
      }

      console.error("Unexpected data format:", data);
      throw new Error(
        "Invalid response format: expected array or object with categories array"
      );
    } catch (error) {
      console.error("Error fetching categories:", error);

      throw error;
    }
  }

  async getProducts(
    page: number = 1,
    limit: number = 6,
    categoryId?: string | null,
    searchQuery?: string,
    cityId?: string,
    outletId?: string | null
  ): Promise<ProductsResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (categoryId) {
        params.append("category", categoryId);
      }

      if (searchQuery && searchQuery.trim()) {
        params.append("search", searchQuery.trim());
      }

      if (cityId && cityId.trim()) {
        params.append("city", cityId);
      }
      if (outletId) {
        params.append("outlet_id", outletId);
      }

      const response = await fetch(
        `${this.baseUrl}/GetCatalog.php?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.products && Array.isArray(data.products)) {
        return {
          products: data.products.map((p: Product) => this.transformProduct(p)),
          totalItems: data.totalItems,
          totalPages: data.totalPages,
        };
      }

      if (Array.isArray(data)) {
        let filtered = [...data];

        if (categoryId) {
          filtered = filtered.filter((p) => p.ID_GOODSGROUP === categoryId);
        }

        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          filtered = filtered.filter(
            (p) =>
              p.NAME.toLowerCase().includes(query) ||
              (p.DESCRIPTION && p.DESCRIPTION.toLowerCase().includes(query))
          );
        }

        const totalItems = filtered.length;
        const totalPages = Math.ceil(totalItems / limit);
        const start = (page - 1) * limit;
        const end = start + limit;
        const products = filtered.slice(start, end);

        return {
          products: products.map((p) => this.transformProduct(p)),
          totalItems,
          totalPages,
        };
      }

      throw new Error("Invalid response format");
    } catch (error) {
      console.error("Error fetching products:", error);

      throw error;
    }
  }

  // private async getMockCategories(): Promise<Category[]> {
  //   await new Promise((resolve) => setTimeout(resolve, 500));

  //   return [
  //     {
  //       ID: "9",
  //       NAME: "Рыбная продукция",
  //       OUTCODE: "376",
  //       PARENT: null,
  //       ID_CASH: 2,
  //       ONLINETRADE: 1,
  //       DESCRIPTION: "Свежая и копченая рыба",
  //     },
  //     {
  //       ID: "10",
  //       NAME: "Мясные деликатесы",
  //       OUTCODE: "377",
  //       PARENT: null,
  //       ID_CASH: 2,
  //       ONLINETRADE: 1,
  //       DESCRIPTION: "Высококачественные мясные продукты",
  //     },
  //     {
  //       ID: "11",
  //       NAME: "Полуфабрикаты",
  //       OUTCODE: "376",
  //       PARENT: null,
  //       ID_CASH: 2,
  //       ONLINETRADE: 1,
  //       DESCRIPTION: "Готовые блюда и полуфабрикаты",
  //     },
  //   ];
  // }

  // private async getMockProducts(): Promise<Product[]> {
  //   await new Promise((resolve) => setTimeout(resolve, 800));

  //   return [
  //     {
  //       ID: "27",
  //       NAME: "Балык форели х/к",
  //       OUTCODE: "1",
  //       PRICE: 1800.0,
  //       MIN_PRICE: 0,
  //       ID_GOODSGROUP: "9",
  //       ID_CASH: 2,
  //       BONUS_FREE: 0,
  //       DESCRIPTION: "Нежный балык из форели холодного копчения",
  //       IS_SERVICE: 0,
  //       ID_ASPECT_SCHEME: 0,
  //       COSTPRICE: 0,
  //       ID_GOODS_MANUFACT: 0,
  //       ONLINETRADE: 0,
  //       SMALL_PHOTO: "",
  //       GENER_PHOTO: "",
  //       EXTERN_PHOTO: "",
  //     },
  //     {
  //       ID: "28",
  //       NAME: "Семга слабосоленая",
  //       OUTCODE: "2",
  //       PRICE: 2500.0,
  //       MIN_PRICE: 0,
  //       ID_GOODSGROUP: "9",
  //       ID_CASH: 2,
  //       BONUS_FREE: 0,
  //       DESCRIPTION: "Свежая семга слабого посола",
  //       IS_SERVICE: 0,
  //       ID_ASPECT_SCHEME: 0,
  //       COSTPRICE: 0,
  //       ID_GOODS_MANUFACT: 0,
  //       ONLINETRADE: 0,
  //     },
  //     {
  //       ID: "29",
  //       NAME: "Форель слабосоленая",
  //       OUTCODE: "3",
  //       PRICE: 2200.0,
  //       MIN_PRICE: 0,
  //       ID_GOODSGROUP: "9",
  //       ID_CASH: 2,
  //       BONUS_FREE: 0,
  //       DESCRIPTION: "Форель слабого посола",
  //       IS_SERVICE: 0,
  //       ID_ASPECT_SCHEME: 0,
  //       COSTPRICE: 0,
  //       ID_GOODS_MANUFACT: 0,
  //       ONLINETRADE: 0,
  //     },
  //     {
  //       ID: "30",
  //       NAME: "Говядина мраморная",
  //       OUTCODE: "4",
  //       PRICE: 3200.0,
  //       MIN_PRICE: 0,
  //       ID_GOODSGROUP: "10",
  //       ID_CASH: 2,
  //       BONUS_FREE: 0,
  //       DESCRIPTION: "Мраморная говядина премиум-класса",
  //       IS_SERVICE: 0,
  //       ID_ASPECT_SCHEME: 0,
  //       COSTPRICE: 0,
  //       ID_GOODS_MANUFACT: 0,
  //       ONLINETRADE: 0,
  //     },
  //   ];
  // }
}

export const catalogApi = new CatalogApi();
